/**
 * Dialog for editing all country data - photos and details
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Cloud,
  ExternalLink,
  FileText,
  Globe,
  GripVertical,
  ImageIcon,
  Images,
  Loader2,
  Map,
  Plus,
  Star,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { CountryLocator } from '@/components/CountryLocator';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  SortableItemWithHandle,
  SortableList,
} from '@/components/ui/sortable-list';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import type { Country } from '@/data/countries/types';
import type { CountryData, CountryDataInput } from '@/hooks/useCountryData';
import type { Photo } from '@/lib/photo-api';
import { cn } from '@/lib/utils';

// Schema for photos
const photoSchema = z.object({
  photo_url: z.string().url('Must be a valid URL'),
  unsplash_id: z.string().optional(),
  photographer: z.string().optional(),
  photographer_url: z.string().optional(),
});

// Schema for country details - all comprehensive fields
const detailsSchema = z.object({
  // Basic info
  description: z.string().optional(),
  capital: z.string().optional(),
  population: z.string().optional(),
  currency: z.string().optional(),
  languages: z.string().optional(),
  area: z.string().optional(),
  // Economic/Quality metrics
  gdp: z.string().optional(),
  hdi: z.string().optional(),
  air_quality_avg: z.string().optional(),
  crime_avg: z.string().optional(),
  // Weather by season
  weather_winter: z.string().optional(),
  weather_spring: z.string().optional(),
  weather_summer: z.string().optional(),
  weather_autumn: z.string().optional(),
  // Other
  timezone: z.string().optional(),
  positives: z.string().optional(), // JSON array as string
  negatives: z.string().optional(), // JSON array as string
  famous_for: z.string().optional(),
});

const formSchema = z.object({
  photos: z.array(photoSchema),
  details: detailsSchema,
});

type FormValues = z.infer<typeof formSchema>;

interface CountryEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  country: Country;
  existingPhotos: Photo[];
  existingData: CountryData | null;
  onSavePhotos: (photos: FormValues['photos']) => Promise<void>;
  onSaveData: (data: CountryDataInput) => Promise<void>;
  loading?: boolean;
}

// Dynamic preview component
function PhotoPreviewThumbnail({
  control,
  index,
}: {
  control: any;
  index: number;
}) {
  const [hasError, setHasError] = useState(false);
  const [imageKey, setImageKey] = useState(0);

  const photoUrl = useWatch({
    control,
    name: `photos.${index}.photo_url`,
  });

  useEffect(() => {
    setHasError(false);
    setImageKey((k) => k + 1);
  }, [photoUrl]);

  if (!photoUrl || hasError) {
    return (
      <div className="w-full aspect-[4/3] rounded-lg bg-muted flex items-center justify-center">
        <ImageIcon className="w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      key={imageKey}
      src={photoUrl}
      alt="Preview"
      className="w-full aspect-[4/3] rounded-lg object-cover"
      onError={() => setHasError(true)}
    />
  );
}

// Photo card for grid layout
function PhotoGridCard({
  index,
  control,
  onRemove,
  handleProps,
  isDragging,
}: {
  index: number;
  control: any;
  onRemove?: () => void;
  handleProps?: object;
  isDragging?: boolean;
}) {
  const photographer = useWatch({
    control,
    name: `photos.${index}.photographer`,
  });
  const photographerUrl = useWatch({
    control,
    name: `photos.${index}.photographer_url`,
  });

  return (
    <div
      className={cn(
        'rounded-lg border bg-card overflow-hidden',
        'group hover:border-primary/50 transition-colors',
        isDragging && 'shadow-xl ring-2 ring-primary/50',
      )}
    >
      <div className="relative">
        <PhotoPreviewThumbnail control={control} index={index} />
        <div className="absolute inset-0 pointer-events-none">
          <div
            {...handleProps}
            className="absolute top-2 left-2 p-1.5 rounded bg-white/90 shadow cursor-grab active:cursor-grabbing touch-none pointer-events-auto"
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/90 shadow text-xs font-medium flex items-center justify-center">
            {index + 1}
          </div>
          {onRemove && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute bottom-2 right-2 w-8 h-8 pointer-events-auto"
              onClick={onRemove}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="p-3 space-y-2">
        <FormField
          control={control}
          name={`photos.${index}.photo_url`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder="https://images.unsplash.com/..."
                  className="font-mono text-xs h-8"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {photographer && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="truncate">Photo by {photographer}</span>
            {photographerUrl && (
              <a
                href={photographerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground shrink-0"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Section header component
function SectionHeader({ icon: Icon, title }: { icon: any; title: string }) {
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
      <Icon className="w-4 h-4" />
      {title}
    </div>
  );
}

export function CountryEditDialog({
  open,
  onOpenChange,
  country,
  existingPhotos,
  existingData,
  onSavePhotos,
  onSaveData,
  loading = false,
}: CountryEditDialogProps) {
  const [activeTab, setActiveTab] = useState('details');
  const [savingPhotos, setSavingPhotos] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      photos: existingPhotos.map((p) => ({
        photo_url: p.photo_url,
        unsplash_id: p.unsplash_id ?? undefined,
        photographer: p.photographer ?? undefined,
        photographer_url: p.photographer_url ?? undefined,
      })),
      details: {
        description: existingData?.description ?? '',
        capital: existingData?.capital ?? '',
        population: existingData?.population ?? '',
        currency: existingData?.currency ?? '',
        languages: existingData?.languages ?? '',
        area: existingData?.area ?? '',
        gdp: existingData?.gdp ?? '',
        hdi: existingData?.hdi ?? '',
        air_quality_avg: existingData?.air_quality_avg ?? '',
        crime_avg: existingData?.crime_avg ?? '',
        weather_winter: existingData?.weather_winter ?? '',
        weather_spring: existingData?.weather_spring ?? '',
        weather_summer: existingData?.weather_summer ?? '',
        weather_autumn: existingData?.weather_autumn ?? '',
        timezone: existingData?.timezone ?? '',
        positives: existingData?.positives ?? '',
        negatives: existingData?.negatives ?? '',
        famous_for: existingData?.famous_for ?? '',
      },
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'photos',
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        photos: existingPhotos.map((p) => ({
          photo_url: p.photo_url,
          unsplash_id: p.unsplash_id ?? undefined,
          photographer: p.photographer ?? undefined,
          photographer_url: p.photographer_url ?? undefined,
        })),
        details: {
          description: existingData?.description ?? '',
          capital: existingData?.capital ?? '',
          population: existingData?.population ?? '',
          currency: existingData?.currency ?? '',
          languages: existingData?.languages ?? '',
          area: existingData?.area ?? '',
          gdp: existingData?.gdp ?? '',
          hdi: existingData?.hdi ?? '',
          air_quality_avg: existingData?.air_quality_avg ?? '',
          crime_avg: existingData?.crime_avg ?? '',
          weather_winter: existingData?.weather_winter ?? '',
          weather_spring: existingData?.weather_spring ?? '',
          weather_summer: existingData?.weather_summer ?? '',
          weather_autumn: existingData?.weather_autumn ?? '',
          timezone: existingData?.timezone ?? '',
          positives: existingData?.positives ?? '',
          negatives: existingData?.negatives ?? '',
          famous_for: existingData?.famous_for ?? '',
        },
      });
    }
  }, [open, existingPhotos, existingData, form]);

  const handleSavePhotos = async () => {
    const photos = form.getValues('photos');
    setSavingPhotos(true);
    try {
      await onSavePhotos(photos);
    } catch (error) {
      console.error('Failed to save photos:', error);
    } finally {
      setSavingPhotos(false);
    }
  };

  const handleSaveDetails = async () => {
    const details = form.getValues('details');
    setSavingDetails(true);
    try {
      await onSaveData({
        description: details.description || undefined,
        capital: details.capital || undefined,
        population: details.population || undefined,
        currency: details.currency || undefined,
        languages: details.languages || undefined,
        area: details.area || undefined,
        gdp: details.gdp || undefined,
        hdi: details.hdi || undefined,
        air_quality_avg: details.air_quality_avg || undefined,
        crime_avg: details.crime_avg || undefined,
        weather_winter: details.weather_winter || undefined,
        weather_spring: details.weather_spring || undefined,
        weather_summer: details.weather_summer || undefined,
        weather_autumn: details.weather_autumn || undefined,
        timezone: details.timezone || undefined,
        positives: details.positives || undefined,
        negatives: details.negatives || undefined,
        famous_for: details.famous_for || undefined,
      });
    } catch (error) {
      console.error('Failed to save details:', error);
    } finally {
      setSavingDetails(false);
    }
  };

  const handleReorder = (reorderedItems: Array<{ id: string }>) => {
    // Get current form values
    const currentPhotos = form.getValues('photos');

    // Create a map of field id to current values
    const fieldValueMap: Record<string, FormValues['photos'][number]> = {};
    fields.forEach((field, index) => {
      fieldValueMap[field.id] = currentPhotos[index];
    });

    // Build new array in the reordered sequence
    const newPhotos = reorderedItems.map((item) => fieldValueMap[item.id]);

    // Replace entire array with new order
    replace(newPhotos);
  };

  // Helper to remove a photo by field ID (more reliable than index after reordering)
  const removeByFieldId = (fieldId: string) => {
    const currentIndex = fields.findIndex((f) => f.id === fieldId);
    if (currentIndex !== -1) {
      remove(currentIndex);
    }
  };

  const sortableItems: Array<{ id: string; index: number }> = fields.map(
    (field, index) => ({
      id: field.id,
      index,
    }),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl h-[85vh] max-h-[800px] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <span className="text-3xl">{country.flag}</span>
            <div>
              <span>{country.name}</span>
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({country.code})
              </span>
            </div>
          </DialogTitle>
          <DialogDescription>
            Edit country information and photos.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex-1 flex flex-col min-h-0"
          >
            <TabsList className="grid w-full grid-cols-3 shrink-0">
              <TabsTrigger value="details" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Details
              </TabsTrigger>
              <TabsTrigger value="photos" className="flex items-center gap-2">
                <Images className="w-4 h-4" />
                Photos ({fields.length})
              </TabsTrigger>
              <TabsTrigger value="map" className="flex items-center gap-2">
                <Map className="w-4 h-4" />
                Map
              </TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent
              value="details"
              className="flex-1 flex flex-col min-h-0 mt-4"
            >
              <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {/* Basic Info Section */}
                    <div>
                      <SectionHeader icon={Globe} title="Basic Information" />
                      <FormField
                        control={form.control}
                        name="details.description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                placeholder="2-3 sentences about what makes this country special..."
                                className="min-h-[80px] resize-none"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <FormField
                          control={form.control}
                          name="details.capital"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Capital</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g., London" />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.population"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Population</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., 67.3 million"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.currency"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Currency</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g., GBP (£)" />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.area"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Area</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., 242,495 km²"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.languages"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Languages</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., English, Welsh"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.timezone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Timezone</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., UTC+0 / UTC+1 (BST)"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Economic & Quality Metrics */}
                    <div>
                      <SectionHeader
                        icon={TrendingUp}
                        title="Economic & Quality Metrics"
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="details.gdp"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>GDP (Nominal)</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., $3.07 trillion"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.hdi"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                HDI (Human Development Index)
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., 0.932 (Very High)"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.air_quality_avg"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Air Quality</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., Moderate (AQI 45)"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.crime_avg"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Crime Rate</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., Low (Safety Index 65)"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Weather by Season */}
                    <div>
                      <SectionHeader icon={Cloud} title="Weather by Season" />
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="details.weather_winter"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Winter</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., Cold and wet, 2-7°C"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.weather_spring"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Spring</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., Mild, 8-15°C"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.weather_summer"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Summer</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., Warm, 15-25°C"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.weather_autumn"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Autumn</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., Cool and rainy, 8-15°C"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Positives & Negatives */}
                    <div>
                      <SectionHeader
                        icon={ThumbsUp}
                        title="Positives & Negatives"
                      />
                      <div className="grid grid-cols-1 gap-4">
                        <FormField
                          control={form.control}
                          name="details.positives"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <ThumbsUp className="w-3.5 h-3.5 text-green-600" />
                                Positives
                              </FormLabel>
                              <FormControl>
                                <Textarea
                                  {...field}
                                  placeholder='JSON array, e.g., ["Great healthcare", "Beautiful nature", "Rich history"]'
                                  className="min-h-[60px] resize-none font-mono text-sm"
                                />
                              </FormControl>
                              <FormDescription>
                                JSON array of strings listing positive aspects
                              </FormDescription>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="details.negatives"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <ThumbsDown className="w-3.5 h-3.5 text-red-600" />
                                Negatives
                              </FormLabel>
                              <FormControl>
                                <Textarea
                                  {...field}
                                  placeholder='JSON array, e.g., ["High cost of living", "Unpredictable weather"]'
                                  className="min-h-[60px] resize-none font-mono text-sm"
                                />
                              </FormControl>
                              <FormDescription>
                                JSON array of strings listing negative aspects
                              </FormDescription>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Famous For */}
                    <div>
                      <SectionHeader icon={Star} title="Famous For" />
                      <FormField
                        control={form.control}
                        name="details.famous_for"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Textarea
                                {...field}
                                placeholder="What is this country famous for? e.g., Fish and chips, the Royal Family, Premier League football, Shakespeare..."
                                className="min-h-[80px] resize-none"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </>
                )}
              </div>

              <DialogFooter className="shrink-0 pt-4 border-t mt-4">
                <div className="flex items-center justify-between w-full">
                  <p className="text-sm text-muted-foreground">
                    {existingData
                      ? 'Last updated: ' +
                        new Date(existingData.updated_at).toLocaleDateString()
                      : 'No data yet'}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSaveDetails}
                      disabled={savingDetails}
                    >
                      {savingDetails && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      Save Details
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </TabsContent>

            {/* Photos Tab */}
            <TabsContent
              value="photos"
              className="flex-1 flex flex-col min-h-0 mt-4"
            >
              <div className="flex-1 overflow-y-auto py-2">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : fields.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No photos yet</p>
                    <p className="text-sm">Add your first photo below.</p>
                  </div>
                ) : (
                  <SortableList<{ id: string; index: number }>
                    items={sortableItems}
                    onReorder={handleReorder}
                    layout="grid"
                    className="grid grid-cols-2 gap-4"
                    renderItem={(item) => (
                      <SortableItemWithHandle key={item.id} id={item.id}>
                        {({ handleProps }: { handleProps: object }) => (
                          <PhotoGridCard
                            index={item.index}
                            control={form.control}
                            onRemove={() => removeByFieldId(item.id)}
                            handleProps={handleProps}
                          />
                        )}
                      </SortableItemWithHandle>
                    )}
                    renderOverlay={(item) => (
                      <PhotoGridCard
                        index={item.index}
                        control={form.control}
                        isDragging
                      />
                    )}
                  />
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-dashed mt-4"
                  onClick={() => append({ photo_url: '' })}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Photo URL
                </Button>
              </div>

              <DialogFooter className="shrink-0 pt-4 border-t">
                <div className="flex items-center justify-between w-full">
                  <p className="text-sm text-muted-foreground">
                    {fields.length} photo{fields.length !== 1 ? 's' : ''}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSavePhotos}
                      disabled={savingPhotos}
                    >
                      {savingPhotos && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      Save Photos
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </TabsContent>

            {/* Map Tab */}
            <TabsContent
              value="map"
              className="flex-1 flex flex-col min-h-0 mt-4"
            >
              <div className="flex-1 rounded-lg border bg-muted/30 overflow-hidden min-h-[400px]">
                <CountryLocator
                  countryName={country.name}
                  countryCode={country.code}
                />
              </div>
              <div className="shrink-0 pt-4 mt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  This shows how the country border will appear in the voting
                  display. Territory merging is handled automatically for
                  disputed regions.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
